/**
 * Samples domain — React Query hooks.
 *
 * Read hooks are re-exported from the generated client, except the two
 * hand-written wrappers (`useGetSamplesInfinite`, `useGetSamplesCounts`)
 * that drf-spectacular/Kubb can't express: an infinite query and a
 * parallel multi-count fan-out.
 *
 * Write hooks go through `useInvalidatingMutation` so cache
 * invalidation is declarative and compile-time-enforced via the
 * `SamplesMutationId` registry in `./invalidation`.
 *
 * The user/product option hooks (`useGetUsers`, `useGetUserOptions`,
 * `useGetProductsOptions`) aren't samples endpoints — they back the
 * Samples filter UI and lived in the old `samplesApi.ts`, so they keep
 * their home here to avoid churning their four consumers.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import type { Product } from '../../models/product'
import { baseAPICall } from '../baseAPICall'
import { clientInstance } from '../../utils/http-client'

import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'
import { useUsers, useUserOptions } from '../users'

import { listSampleRequests } from '../generated/clients/listSampleRequests'
import { createSampleRequest } from '../generated/clients/createSampleRequest'
import { updateSampleRequest } from '../generated/clients/updateSampleRequest'
import { cancelSampleRequest } from '../generated/clients/cancelSampleRequest'
import { reviewSampleRequest } from '../generated/clients/reviewSampleRequest'
import { updateSampleFulfillment } from '../generated/clients/updateSampleFulfillment'
import { exportSampleRequestsV2 } from '../generated/clients/exportSampleRequestsV2'

import type { ListSampleRequestsQueryParams } from '../generated/types/ListSampleRequests'
import type { CreateSampleRequestMutationRequest } from '../generated/types/CreateSampleRequest'
import type {
  UpdateSampleRequestMutationRequest,
  UpdateSampleRequestPathParams,
} from '../generated/types/UpdateSampleRequest'
import type {
  CancelSampleRequestMutationRequest,
  CancelSampleRequestPathParams,
} from '../generated/types/CancelSampleRequest'
import type {
  ReviewSampleRequestMutationRequest,
  ReviewSampleRequestPathParams,
} from '../generated/types/ReviewSampleRequest'
import type {
  UpdateSampleFulfillmentMutationRequest,
  UpdateSampleFulfillmentPathParams,
} from '../generated/types/UpdateSampleFulfillment'
import type { ExportSampleRequestsV2MutationRequest } from '../generated/types/ExportSampleRequestsV2'

// ── Generated query-hook re-exports (READ) ─────────────────────────

export {
  useGetSampleRequest,
  getSampleRequestQueryKey,
  useListSampleRequests,
  listSampleRequestsQueryKey,
} from '../generated/hooks/samplesController'

// ── Generated client-function re-exports (imperative consumers) ────

export { listSampleRequests } from '../generated/clients/listSampleRequests'
export { getSampleRequest } from '../generated/clients/getSampleRequest'
export { exportSampleRequestsV2 } from '../generated/clients/exportSampleRequestsV2'

// ── Generated query/mutation type re-exports ───────────────────────

export type {
  ListSampleRequestsQueryParams,
  ListSampleRequestsQueryResponse,
} from '../generated/types/ListSampleRequests'
export type {
  GetSampleRequestQueryResponse,
  GetSampleRequestPathParams,
} from '../generated/types/GetSampleRequest'
export type {
  CreateSampleRequestMutationRequest,
  CreateSampleRequestMutationResponse,
} from '../generated/types/CreateSampleRequest'
export type {
  UpdateSampleRequestMutationRequest,
  UpdateSampleRequestMutationResponse,
} from '../generated/types/UpdateSampleRequest'
export type {
  CancelSampleRequestMutationRequest,
  CancelSampleRequestMutationResponse,
} from '../generated/types/CancelSampleRequest'
export type {
  ReviewSampleRequestMutationRequest,
  ReviewSampleRequestMutationResponse,
} from '../generated/types/ReviewSampleRequest'
export type {
  UpdateSampleFulfillmentMutationRequest,
  UpdateSampleFulfillmentMutationResponse,
} from '../generated/types/UpdateSampleFulfillment'
export type {
  ExportSampleRequestsV2MutationRequest,
  ExportSampleRequestsV2MutationResponse,
} from '../generated/types/ExportSampleRequestsV2'

// ── Hand-written read hooks ────────────────────────────────────────

/**
 * Sample requests with infinite-scroll pagination (mobile list).
 *
 * Wraps the generated `listSampleRequests` client fn in
 * `useInfiniteQuery` — Kubb doesn't emit infinite-query variants.
 * Pagination keys off the DRF `next` link rather than a page count:
 * the paginated response only carries `count`/`next`/`previous`, so
 * the next page is `allPages.length + 1` while `next` is non-null.
 */
export function useGetSamplesInfinite(
  params: ListSampleRequestsQueryParams = {},
  enabled: boolean = true
) {
  return useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ['samples-infinite', params] as const,
    queryFn: ({ pageParam }) =>
      listSampleRequests({ ...params, page: pageParam as number }),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.next ? allPages.length + 1 : undefined,
    enabled,
  })
}

/**
 * Tab badge counts for the Samples list.
 *
 * `needsMyReview` controls whether the third count (broker-review
 * queue) is fetched. Callers that won't surface that badge leave it
 * false to skip the extra request. Fires the 2-3 count requests in
 * parallel, each a `page_size: 1` probe read for its `.count`.
 */
export function useGetSamplesCounts(
  params: ListSampleRequestsQueryParams = {},
  { needsMyReview = false }: { needsMyReview?: boolean } = {}
) {
  return useQuery({
    queryKey: ['samples-counts', params, { needsMyReview }],
    queryFn: async () => {
      const requests = [
        listSampleRequests({ ...params, page: 1, page_size: 1 }),
        listSampleRequests({
          ...params,
          page: 1,
          page_size: 1,
          my_requests_only: true,
        }),
        ...(needsMyReview
          ? [
              listSampleRequests({
                ...params,
                page: 1,
                page_size: 1,
                status: ['pending_review'],
                needs_my_review: true,
              }),
            ]
          : []),
      ]
      const [allResponse, myResponse, needsReviewResponse] =
        await Promise.all(requests)
      return {
        all_samples_count: allResponse.count,
        my_samples_count: myResponse.count,
        needs_my_review_count: needsReviewResponse?.count ?? 0,
      }
    },
  })
}

// ── Samples filter-option hooks (non-endpoint helpers) ─────────────

/** @deprecated Use useUsers from services/users directly */
export const useGetUsers = (disabled?: boolean) =>
  useUsers(
    undefined,
    disabled !== undefined ? { query: { enabled: !disabled } } : {}
  )

/** @deprecated Use useUserOptions from services/users directly */
export const useGetUserOptions = useUserOptions

async function getProductsOptions(withNoneOption?: boolean) {
  return baseAPICall<
    {
      label: string
      value: number
      original: Product
    }[]
  >(() =>
    clientInstance.get('products/', {
      transformResponse: (data) => {
        const response = JSON.parse(data)
        // Handle paginated response structure
        const products = response.results || response
        const productOptions: {
          label: string
          value: number
          original: Product
        }[] = products.map((product: Product) => ({
          label: product.name,
          value: product.id,
          original: product,
        }))

        if (withNoneOption) {
          productOptions.unshift({
            label: 'No Product',
            value: -1,
            original: {} as Product,
          })
        }

        return productOptions
      },
    })
  )
}

/** Product options shaped for `SelectFilter`. */
export const useGetProductsOptions = (withNoneOption?: boolean) =>
  useQuery({
    queryKey: ['productsOptions', withNoneOption ? 'with-none' : ''],
    queryFn: () => getProductsOptions(withNoneOption),
  })

// ── Mutation hooks with auto-invalidation (WRITE) ──────────────────

export function useCreateSampleRequest() {
  return useInvalidatingMutation('createSampleRequest', invalidationRegistry, {
    mutationFn: (variables: { data: CreateSampleRequestMutationRequest }) =>
      createSampleRequest(variables.data),
  })
}

export function useUpdateSampleRequest() {
  return useInvalidatingMutation('updateSampleRequest', invalidationRegistry, {
    mutationFn: (variables: {
      id: UpdateSampleRequestPathParams['id']
      data: UpdateSampleRequestMutationRequest
    }) => updateSampleRequest({ id: variables.id }, variables.data),
  })
}

export function useCancelSampleRequest() {
  return useInvalidatingMutation('cancelSampleRequest', invalidationRegistry, {
    mutationFn: (variables: {
      id: CancelSampleRequestPathParams['id']
      data: CancelSampleRequestMutationRequest
    }) => cancelSampleRequest({ id: variables.id }, variables.data),
  })
}

export function useReviewSampleRequest() {
  return useInvalidatingMutation('reviewSampleRequest', invalidationRegistry, {
    mutationFn: (variables: {
      id: ReviewSampleRequestPathParams['id']
      data: ReviewSampleRequestMutationRequest
    }) => reviewSampleRequest({ id: variables.id }, variables.data),
  })
}

/**
 * FIR-7033: manually set a sample's fulfillment status + tracking info.
 * PATCHes only the provided fields; not for the review/cancel lifecycle.
 */
export function useUpdateSampleFulfillment() {
  return useInvalidatingMutation(
    'updateSampleFulfillment',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        id: UpdateSampleFulfillmentPathParams['id']
        data: UpdateSampleFulfillmentMutationRequest
      }) => updateSampleFulfillment({ id: variables.id }, variables.data),
    }
  )
}

export function useExportSampleRequestsV2() {
  return useInvalidatingMutation(
    'exportSampleRequestsV2',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: ExportSampleRequestsV2MutationRequest
      }) => exportSampleRequestsV2(variables.data),
    }
  )
}
